import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router";
import { PageLoader } from "./components/ui/spinner";
import { useLanguageSync, useMe } from "./hooks/useMe";
import { ApiError } from "./lib/api";
import { AppShell } from "./layouts/AppShell";
import { NAV } from "./lib/nav";
import { Landing } from "./pages/Landing";
import { NotFound } from "./pages/NotFound";
import { Login } from "./pages/auth/Login";
import { Register } from "./pages/auth/Register";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { CheckEmail } from "./pages/auth/CheckEmail";
import { Overview } from "./pages/app/Overview";
import { ModuleComingSoon } from "./pages/app/ModuleComingSoon";
import { Button } from "./components/ui/button";
import { t } from "@shared/i18n";

function SessionGate({ requireOnboarded }: { requireOnboarded: boolean }) {
  const { data, error, isLoading, refetch } = useMe();
  const location = useLocation();
  useLanguageSync(data);
  if (isLoading) return <PageLoader />;
  if (error instanceof ApiError && error.status === 401) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (error || !data) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-muted">{error?.message ?? t("Impossible de charger votre compte.")}</p>
        <Button onClick={() => refetch()}>{t("Réessayer")}</Button>
      </div>
    );
  }
  const onboarded = Boolean(data.profile.onboardingCompletedAt);
  if (requireOnboarded && !onboarded) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

/** Redirige un utilisateur déjà connecté hors des pages de connexion. */
function GuestOnly() {
  const { data, isLoading } = useMe();
  if (isLoading) return <PageLoader />;
  if (data) return <Navigate to="/app" replace />;
  return <Outlet />;
}

export const router = createBrowserRouter(
  [
  { path: "/", element: <Landing /> },
  { path: "/legal/:page?", lazy: () => import("./pages/Legal").then((m) => ({ Component: m.Legal })) },
  { path: "/help", lazy: () => import("./pages/Help").then((m) => ({ Component: m.Help })) },
  {
    element: <GuestOnly />,
    children: [
      { path: "/login", element: <Login /> },
      { path: "/register", element: <Register /> },
      { path: "/forgot-password", element: <ForgotPassword /> },
    ],
  },
  { path: "/reset-password", element: <ResetPassword /> },
  { path: "/invite/:token", lazy: () => import("./pages/Invite").then((m) => ({ Component: m.Invite })) },
  { path: "/check-email", element: <CheckEmail /> },
  { element: <SessionGate requireOnboarded={false} />, children: [{ path: "/onboarding", lazy: () => import("./pages/Onboarding").then((m) => ({ Component: m.Onboarding })) }] },
  {
    path: "/app",
    element: <SessionGate requireOnboarded />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Overview /> },
          { path: "tasks", lazy: () => import("./pages/app/Tasks").then((m) => ({ Component: m.Tasks })) },
          { path: "planning", lazy: () => import("./pages/app/Planning").then((m) => ({ Component: m.Planning })) },
          { path: "finance", lazy: () => import("./pages/app/finance/Finance").then((m) => ({ Component: m.Finance })) },
          { path: "documents", lazy: () => import("./pages/app/documents/Documents").then((m) => ({ Component: m.Documents })) },
          { path: "documents/:id", lazy: () => import("./pages/app/documents/DocumentEditor").then((m) => ({ Component: m.DocumentEditor })) },
          { path: "files", lazy: () => import("./pages/app/Files").then((m) => ({ Component: m.Files })) },
          { path: "goals", lazy: () => import("./pages/app/Goals").then((m) => ({ Component: m.Goals })) },
          { path: "notifications", lazy: () => import("./pages/app/Notifications").then((m) => ({ Component: m.Notifications })) },
          { path: "missions", lazy: () => import("./pages/app/Missions").then((m) => ({ Component: m.Missions })) },
          { path: "coach/:conversationId?", lazy: () => import("./pages/app/Coach").then((m) => ({ Component: m.Coach })) },
          { path: "team", lazy: () => import("./pages/app/Team").then((m) => ({ Component: m.Team })) },
          { path: "integrations", lazy: () => import("./pages/app/Integrations").then((m) => ({ Component: m.Integrations })) },
          { path: "reminders", lazy: () => import("./pages/app/Reminders").then((m) => ({ Component: m.Reminders })) },
          { path: "marketing", lazy: () => import("./pages/app/Marketing").then((m) => ({ Component: m.Marketing })) },
          { path: "meetings", lazy: () => import("./pages/app/Meetings").then((m) => ({ Component: m.Meetings })) },
          { path: "admin", lazy: () => import("./pages/app/Admin").then((m) => ({ Component: m.Admin })) },
          { path: "automations", lazy: () => import("./pages/app/Automations").then((m) => ({ Component: m.Automations })) },
          { path: "settings/:tab?", lazy: () => import("./pages/settings/Settings").then((m) => ({ Component: m.Settings })) },
          ...NAV.filter((n) => n.phase).map((n) => ({ path: n.to.replace("/app/", ""), element: <ModuleComingSoon item={n} /> })),
        ],
      },
    ],
  },
  { path: "*", element: <NotFound /> },
  ],
);
